/**
 * A drag and drop video upload box.
 * Can be used with v-model to provide the resultant URL for the upload.
 */

<template>
  <vue-transmit v-bind="options"
                ref="uploader"
                @added-files="addedFiles"
                @accepted-file="acceptedFile"
                @error="error"
                class="v-transmit"
                :class="disabled ? 'disabled' : ''"
                style="text-align: center;"
  >
    <slot></slot>
  </vue-transmit>
</template>

<script>
import Vue from 'vue';
import VueTransmit from 'vue-transmit';
import 'vue-transmit/dist/vue-transmit.css';
import variable from '@/config/variables';
import VT_ERROR from './error';

Vue.use(VueTransmit);

export default {
  props: {
    submissionId: {
      type: Number,
      required: true,
    },
    /**
     * Callback that will be called once the video upload is completed.
     * @param {string} awsPath
     * @param {fileMeta}
     */
    backgroundUploadFn: {
      type: Function,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Component Variables
      options: {
        // [Default]
        accept: this.testAcceptance,
        //  Prevent vue-transmit default behaviour
        autoProcessQueue: false,
        url: '',
        clickable: false,
        // [Constrains]
        //  Use either file extension or media-types
        acceptedFileTypes: [
          '.mp4',
          '.avi',
          '.mov',
        ],
        //  AWS's single object maximum size
        maxFileSize: 4999999,
        // [Error text]
        dictFileTooBig: VT_ERROR.make(VT_ERROR.code.FILE_TOO_BIG),
        dictInvalidFileType: VT_ERROR.make(VT_ERROR.code.INVALID_FILE_TYPE),
        dictResponseError: VT_ERROR.make(VT_ERROR.code.RESPONSE_ERROR),
        dictMaxFilesExceeded: VT_ERROR.make(VT_ERROR.code.MAX_FILES_EXCEEDED),
      },
    };
  },
  methods: {
    /**
     * This section contains all the Vue Transmit related methods.
     */
    openSystemBrowseDialogue() {
      this.$refs.uploader.triggerBrowseFiles();
    },
    generateFileMeta(vTransmitFile) {
      // Initialise file information
      const fileMeta = {
        id: this.submissionId,
        ext: vTransmitFile.name.split('.').pop(),
        size: vTransmitFile.size,
        file: vTransmitFile.nativeFile,
      };
      fileMeta.path = `${variable.AWS_FOLDER}/${fileMeta.id}/`;
      fileMeta.name = `${fileMeta.id}.${fileMeta.ext}`;
      return fileMeta;
    },
    addedFiles(vTransmitFiles) {
      this.intermediateAddedFileCount = vTransmitFiles.length;
    },
    testAcceptance(vTransmitFile, done) {
      // Single item upload test
      if (this.intermediateAddedFileCount > 1 ||
          (this.$store.state.uploadQueue[this.submissionId] && this.$store.state.uploadQueue[this.submissionId].video)
      ) {
        done(VT_ERROR.make(VT_ERROR.code.ONLY_ONE_FILE)); return;
      }
      { // File size test
        const FILE_SIZE_WARN_LIMIT = 2000;
        const fileSize = vTransmitFile.size / 1000 / 1000;
        if (fileSize > FILE_SIZE_WARN_LIMIT) {
          done(VT_ERROR.make(VT_ERROR.code.FILE_IS_BIG, fileSize.toFixed(), FILE_SIZE_WARN_LIMIT)); return;
        }
      }
      done();
    },
    acceptedFile(vTransmitFile) {
      if (this.disabled) return;

      const fileMeta = this.generateFileMeta(vTransmitFile);
      this.$store.dispatch('uploadQueue/enqueueVideo', fileMeta)
        .then((awsPath) => {
          this.$emit('input', awsPath);
          this.$emit('submit');
          this.backgroundUploadFn(awsPath, fileMeta);
        });
    },

    error(VTransmitFile, message) {
      // Deconstruct error message
      const error = VT_ERROR.break(message);

      // Preprocess errors
      if (error.code === VT_ERROR.code.FILE_IS_BIG) {
        // Ignore error
        this.acceptedFile(VTransmitFile);
        return;
      } else if (error.code === VT_ERROR.code.INVALID_FILE_TYPE) {
        error.message = VT_ERROR.fill(error.message,
          this.options.acceptedFileTypes
            .map((fileMeta) => {
              const parts = fileMeta.split('/');
              const general = parts[0];
              const ext = parts[1];
              return (parts[1] && parts[1].length > 1) ? ext : general;
            })
            .join(', ')
        );
      }

      this.$alert(
        error.message,
        'Opps!',
        {
          confirmButtonText: 'Ok',
        },
      );
    },
  },

  mounted() {
  },
};
</script>

<style scoped>
.bg-faded {
  background-color: gray;
}

.progress-bar {
  transition: width 1s;
}
</style>

<style lang="scss">
.v-transmit.disabled {
  cursor: not-allowed;
  .v-transmit__upload-area {
    background-color: #f5f7fa;
    border-color: #dfe4ed;
    color: #b4bccc;

    > * {
      pointer-events: none;
      opacity: 0.8;
    }
  }
}
.v-transmit .v-transmit__upload-area {
  min-height: 360px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  background: rgba(255, 255, 255, 1);
  padding: 20px;
  padding-top: 50px;

  /* Override default styling */
  border: 1px dashed #bdbdbd;
  border-radius: 6px;
  box-sizing: border-box;

  &--is-dragging {
    border: 2px dashed #bdbdbd;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/Submission/steps/VideoUpload/VideoUploader.vue